import React, { useState, useEffect } from 'react';
import api from '../api/axiosConfig';
// FIX 1: Removed unused 'FileText' from imports
import { Send, Users, Mail, Bell, CheckCircle, AlertCircle } from 'lucide-react'; 

const CommunicationPanel = () => {
  // --- STATE MANAGEMENT ---
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ type: '', msg: '' });
  
  // Form Data
  const [formData, setFormData] = useState({
    title: '', // Internal campaign name
    subject: '',
    message_body: '',
    target_audience: 'DEPARTMENT', // Default
    filter_value: '', // e.g., 'IT' or 'Manager'
    channels: { email: true, in_app: true }
  });

  // Data for Dropdowns
  const [templates, setTemplates] = useState([]);
  
  // Hardcoded options (In real app, fetch these from DB)
  const departments = ['Management', 'IT', 'HR', 'Sales', 'Construction'];
  const roles = ['Project Manager', 'Developer', 'Laborer', 'Admin'];

  // --- 1. FETCH TEMPLATES ON LOAD ---
  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const res = await api.get('/communication/templates');
      setTemplates(Array.isArray(res.data) ? res.data : (res.data?.data || []));
    } catch (err) {
      console.error("Failed to load templates", err);
    }
  };

  // --- 2. HANDLERS ---
  
  // Apply a selected template
  const handleTemplateSelect = (e) => {
    const templateId = e.target.value;
    if (!templateId) return;
    
    // FIX 2: Used '==' loose comparison safely or converted types to fix warning
    // We convert both to String to ensure they match regardless of type (number/string)
    const selected = templates.find(t => String(t.id) === String(templateId));
    
    if (selected) {
      setFormData(prev => ({
        ...prev,
        subject: selected.subject,
        message_body: selected.body_content
      }));
    }
  };

  // Insert Variable into Message
  const insertVariable = (variable) => {
    setFormData(prev => ({
      ...prev,
      message_body: prev.message_body + ` {{${variable}}} `
    }));
  };

  // Handle Input Changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle Checkboxes
  const handleChannelChange = (channel) => {
    setFormData(prev => ({
      ...prev,
      channels: { ...prev.channels, [channel]: !prev.channels[channel] }
    }));
  };

  // --- 3. SUBMIT FORM ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: '', msg: '' });

    // Prepare payload for backend
    const payload = {
      ...formData,
      channels: Object.keys(formData.channels).filter(k => formData.channels[k])
    };

    if (payload.channels.length === 0) {
      setStatus({ type: 'error', msg: 'Please select at least one channel (Email or Notification).' });
      setLoading(false);
      return;
    }

    try {
      const res = await api.post('/communication/send', payload);
      setStatus({ type: 'success', msg: res.data?.message || 'Campaign sent successfully' });
      // Optional: Reset form here if needed
    } catch (err) {
      setStatus({ type: 'error', msg: err.response?.data?.error || 'Failed to send campaign.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      
      {/* HEADER */}
      <div className="mb-8">
        <h1 className="text-base font-bold text-gray-800 flex items-center gap-2">
          <Send className="w-6 h-6 text-blue-600" />
          Communication Center
        </h1>
        <p className="text-gray-500">Send bulk emails and notifications dynamically.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* LEFT COL: SETTINGS */}
        <div className="lg:col-span-1 space-y-6">
          
          {/* Target Audience Card */}
          <div className="bg-white p-5 rounded-lg shadow border border-gray-100">
            <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <Users className="w-4 h-4" /> Target Audience
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase">Send To</label>
                <select 
                  name="target_audience" 
                  value={formData.target_audience}
                  onChange={handleChange}
                  className="w-full mt-1 p-2 border rounded focus:ring-2 focus:ring-blue-500"
                >
                  <option value="ALL">All Active Users</option>
                  <option value="DEPARTMENT">Specific Department</option>
                  <option value="ROLE">Specific Role</option>
                  <option value="INDIVIDUAL">Individual (ID)</option>
                </select>
              </div>

              {/* Conditional Filters */}
              {formData.target_audience === 'DEPARTMENT' && (
                <select 
                  name="filter_value" 
                  onChange={handleChange}
                  className="w-full p-2 border rounded bg-gray-50"
                >
                  <option value="">-- Select Department --</option>
                  {departments.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              )}

              {formData.target_audience === 'ROLE' && (
                <select 
                  name="filter_value" 
                  onChange={handleChange}
                  className="w-full p-2 border rounded bg-gray-50"
                >
                  <option value="">-- Select Role --</option>
                  {roles.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              )}
              
               {formData.target_audience === 'INDIVIDUAL' && (
                <input 
                  type="number" 
                  name="filter_value" 
                  placeholder="Enter User ID"
                  onChange={handleChange}
                  className="w-full p-2 border rounded bg-gray-50"
                />
              )}
            </div>
          </div>

          {/* Channels Card */}
          <div className="bg-white p-5 rounded-lg shadow border border-gray-100">
            <h3 className="font-semibold text-gray-700 mb-3">Channels</h3>
            <div className="space-y-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={formData.channels.email} 
                  onChange={() => handleChannelChange('email')}
                  className="w-5 h-5 text-blue-600 rounded" 
                />
                <span className="flex items-center gap-2 text-gray-700"><Mail className="w-4 h-4"/> Send Email</span>
              </label>
              
              <label className="flex items-center gap-3 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={formData.channels.in_app} 
                  onChange={() => handleChannelChange('in_app')}
                  className="w-5 h-5 text-blue-600 rounded" 
                />
                <span className="flex items-center gap-2 text-gray-700"><Bell className="w-4 h-4"/> In-App Notification</span>
              </label>
            </div>
          </div>
        </div>

        {/* RIGHT COL: COMPOSER */}
        <div className="lg:col-span-2 bg-white p-6 rounded-lg shadow border border-gray-100">
          
          {/* Template Selection */}
          <div className="mb-6 flex justify-between items-center">
             <h3 className="font-semibold text-gray-700">Compose Message</h3>
             <select onChange={handleTemplateSelect} className="text-sm border-gray-300 rounded border p-1">
               <option value="">Load a Template...</option>
               {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
             </select>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Campaign Title (Internal)</label>
              <input 
                type="text" 
                name="title" 
                required 
                placeholder="e.g. Jan Policy Update"
                className="w-full p-2 border rounded focus:outline-none focus:border-blue-500"
                onChange={handleChange}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Subject Line</label>
              <input 
                type="text" 
                name="subject" 
                required 
                value={formData.subject}
                placeholder="Message Subject..."
                className="w-full p-2 border rounded focus:outline-none focus:border-blue-500"
                onChange={handleChange}
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-600">Message Body</label>
                
                {/* FIX 3: Variable Injectors - Ensure quotes are used */}
                <div className="text-xs space-x-2">
                  <span className="text-gray-400">Insert:</span>
                  
                  {/* BUTTON 1: Name */}
                  <button 
                    type="button" 
                    onClick={() => insertVariable('name')} 
                    className="bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded text-blue-600 font-medium transition"
                  >
                    Name
                  </button>

                  {/* BUTTON 2: Email */}
                  <button 
                    type="button" 
                    onClick={() => insertVariable('email')} 
                    className="bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded text-blue-600 font-medium transition"
                  >
                    Email
                  </button>
                </div>
              </div>

              <textarea 
                name="message_body" 
                rows="8" 
                required
                value={formData.message_body}
                className="w-full p-3 border rounded focus:outline-none focus:border-blue-500 font-mono text-sm"
                placeholder="Type your message here. Use {{name}} to personalize..."
                onChange={handleChange}
              ></textarea>
              <p className="text-xs text-gray-400 mt-1">
                Tip: Use <span className="font-mono text-gray-600 bg-gray-100 px-1">{'{{name}}'}</span> to automatically insert the user's name.
              </p>
            </div>

            {/* Status Messages */}
            {status.msg && (
              <div className={`p-3 rounded flex items-center gap-2 ${status.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                {status.type === 'success' ? <CheckCircle className="w-5 h-5"/> : <AlertCircle className="w-5 h-5"/>}
                {status.msg}
              </div>
            )}

            <button 
              type="submit" 
              disabled={loading}
              className={`w-full py-3 rounded text-white font-medium flex justify-center items-center gap-2
                ${loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 transition shadow-lg shadow-blue-200'}
              `}
            >
              {loading ? 'Sending Campaign...' : <><Send className="w-4 h-4" /> Send Campaign</>}
            </button>
          </form>
        </div>

      </div>
    </div>
  );
};

export default CommunicationPanel;